import { useCallback, useEffect, useState } from "react";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const ROLES = ["admin", "hr_manager", "dept_head", "employee"];

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "employee", employee_id: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data.users)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, employee_id: form.employee_id || null });
      toast.success(`User ${form.email} created`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "employee", employee_id: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleActive = async (id, active) => {
    try {
      await api.patch(`/users/${id}`, { active });
      toast.success(active ? "User activated" : "User deactivated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5 fade-in" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-muted-foreground">Platform accounts and role assignments.</p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="users-create-btn" className="rounded-none bg-teal-700 hover:bg-teal-800 text-white h-9 text-[13px]">
              <UserPlus className="h-4 w-4" /> Create user
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none sm:max-w-md" data-testid="users-create-dialog">
            <DialogHeader>
              <DialogTitle className="text-[15px] font-bold text-[var(--brand-primary)]">Create / invite user</DialogTitle>
            </DialogHeader>
            <form onSubmit={createUser} className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Full name</Label>
                <Input data-testid="users-form-name" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Email</Label>
                <Input data-testid="users-form-email" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none h-9" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Temporary password (min 8 chars)</Label>
                <PasswordInput data-testid="users-form-password" required minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none h-9 font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="rounded-none h-9 text-[13px]" data-testid="users-form-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {ROLES.map((r) => <SelectItem key={r} value={r} className="rounded-none text-[13px]">{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Employee ID (optional)</Label>
                  <Input data-testid="users-form-employee-id" placeholder="EMP-001" value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="rounded-none h-9 font-mono" />
                </div>
              </div>
              <Button type="submit" disabled={saving} data-testid="users-form-submit"
                className="w-full rounded-none bg-[var(--brand-primary)] hover:bg-slate-700 text-white h-9">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create user"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!users ? (
        <Skeleton className="h-72 w-full rounded-none" />
      ) : (
        <div className="bg-white border border-border">
          <Table data-testid="users-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="text-[11px] uppercase tracking-[0.1em] pl-5">User</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em]">Role</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em]">Linked Employee</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em]">Created</TableHead>
                <TableHead className="text-[11px] uppercase tracking-[0.1em] text-right pr-5">Active</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-border hover:bg-slate-50/70" data-testid={`user-row-${u.email}`}>
                  <TableCell className="pl-5">
                    <p className="text-[13px] font-medium text-slate-800">{u.name}</p>
                    <p className="text-[11px] font-mono text-muted-foreground">{u.email}</p>
                  </TableCell>
                  <TableCell>
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                      <SelectTrigger className="rounded-none h-8 w-36 text-[12px]" data-testid={`user-role-select-${u.email}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none">
                        {ROLES.map((r) => <SelectItem key={r} value={r} className="rounded-none text-[12px]">{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="font-mono text-[12px] text-slate-600">{u.employee_id || "—"}</TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("en-GB") : "—"}
                  </TableCell>
                  <TableCell className="text-right pr-5">
                    <Switch
                      checked={u.active !== false}
                      disabled={u.id === me.id}
                      onCheckedChange={(v) => toggleActive(u.id, v)}
                      data-testid={`user-active-switch-${u.email}`}
                      className="data-[state=checked]:bg-teal-600"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
